"use client";
import React, { useState, useEffect, useRef } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { Menu, Icon } from "semantic-ui-react";
import PlacesAutocomplete from "react-places-autocomplete";
import cookie from "react-cookies";
import { getNHmaphomelist } from "../../../modules/Graphql/Nhmaphomegraphql";
import { getSearchNeighbors } from "../../../modules/Graphql/neighboraddressserachgraphql";
import { getSchoolinNh } from "../../../modules/Graphql/neighborhoodboundriesgraphql";
import { getcitydata } from "../../../modules/Graphql/cityquestionandanswergraphql";

// Helper function to check if string starts with number
const startsWithNumber = (str) => {
  return /^\d/.test(str);
};

const onError = (status, clearSuggestions) => {};

// ClientSearchBar component - matches Layout component's search functionality
// Props:
// - nhmap: (optional) boolean - enables map interaction mode instead of navigation
// - searchOnSelect: (optional) function - callback when item selected in map mode (required if nhmap=true and using map interactions)
const ClientSearchBar = ({ nhmap = false, searchOnSelect = null }) => {
  // Component mounting state
  const [mounted, setMounted] = useState(false);

  // Search input states
  const [address, setAddress] = useState("");
  const [userInput, setUserInput] = useState("");
  const [query, setQuery] = useState("");

  // Search results
  const [results, setResults] = useState([]);
  const [homeresults, setHomeresults] = useState([]);

  // UI states
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [is_loaded, setIsLoaded] = useState(true);
  const [is_selected, setIsSelected] = useState(false);
  const [is_suggestion_click, setIsSuggestionClick] = useState(false);
  const [is_mls_calling, setIsMlsCalling] = useState(false);
  const [isnhmap, setIsnhmap] = useState(false);
  const [gmapsLoaded, setGmapsLoaded] = useState(false);
  const [bottomtabwidth, setBottomtabwidth] = useState(0);

  // Refs for DOM manipulation
  const ref = useRef();
  const suggestiondata = useRef([]);

  // Initialize component
  useEffect(() => {
    setMounted(true);

    // Check for Google Maps availability
    if (typeof window !== "undefined") {
      if (window.google) {
        setGmapsLoaded(true);
      } else {
        // Wait for Google Maps to load
        const checkGoogleMaps = () => {
          if (window.google) {
            setGmapsLoaded(true);
          } else {
            setTimeout(checkGoogleMaps, 100);
          }
        };
        checkGoogleMaps();
      }
    }

    // Add click outside handler
    const handleClickOutside = (event) => {
      if (
        event.target.className !== "location-search-input focus-visible" &&
        event.target.className !== "suggestions_name" &&
        event.target.className !== "suggestion-active"
      ) {
        setShowSuggestions(false);
      } else {
        setShowSuggestions(true);
      }
    };

    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Fetch home data - exactly like Layout component
  const gethomedataInfo = (searchquery) => {
    setIsLoaded(false);

    if (searchquery !== "") {
      const getneighbordata = {
        start: 0,
        limit: 5,
        orderBy: "label",
        order: "ASC",
        fields: [{ name: "label", value: searchquery, operator: "LIKE" }],
      };

      const getdata = {
        start: 0,
        limit: 5,
        orderBy: "UnparsedAddress",
        order: "Like",
        fields: [
          { name: "UnparsedAddress", value: searchquery, operator: "LIKE" },
        ],
      };

      const getdataschool = {
        start: 0,
        limit: 5,
        orderBy: "School",
        order: "Like",
        fields: [{ name: "School", value: searchquery, operator: "LIKE" }],
      };

      Promise.all([
        getNHmaphomelist(getdata),
        getSearchNeighbors(getneighbordata),
        getSchoolinNh(getdataschool),
      ])
        .then((result) => {
          if (searchquery === "") {
            setResults([]);
            setHomeresults([]);
            setIsSuggestionClick(false);
            setActiveSuggestion(0);
            setIsLoaded(true);
            setShowSuggestions(false);
          } else {
            let righthomedata = [];

            // Safely handle home data
            if (
              result[0]?.data?.getnhmaphome &&
              Array.isArray(result[0].data.getnhmaphome) &&
              result[0].data.getnhmaphome.length > 0
            ) {
              righthomedata.push(...result[0].data.getnhmaphome);
            }
            setHomeresults(righthomedata);

            // Safely handle neighborhood data
            let nhresultsdata = [];
            if (result[1]?.data?.getSearchNeighbors) {
              nhresultsdata = Array.isArray(result[1].data.getSearchNeighbors)
                ? result[1].data.getSearchNeighbors
                : [];
            }

            // Safely handle school data
            let schoolresultdata = [];
            if (result[2]?.data?.getSchoolinNh) {
              schoolresultdata = Array.isArray(result[2].data.getSchoolinNh)
                ? result[2].data.getSchoolinNh
                : [];
            }

            // Combine all results
            let finalresult = [
              ...(nhresultsdata || []),
              ...(righthomedata || []),
              ...(schoolresultdata || []),
            ];

            if (searchquery !== "") {
              setResults(finalresult);
            }

            // Set loading state
            if (is_mls_calling) {
              setIsLoaded(false);
            } else {
              setIsLoaded(true);
            }
          }

          // Show suggestions when we have results (exactly like Layout)
          if (!is_selected) {
            setShowSuggestions(true);
          }
        })
        .catch((error) => {
          setResults([]);
          setHomeresults([]);
          setActiveSuggestion(0);
          setIsLoaded(true);
          console.error("GraphQL search error:", error);
        });
    } else {
      setResults([]);
      setHomeresults([]);
      setIsSuggestionClick(false);
      setIsLoaded(true);
      setShowSuggestions(false);
    }
  };

  // Handle input change - exactly like Layout component
  const handleInputChange = (address) => {
    let searchquery = address;
    setIsMlsCalling(false);

    if (is_selected) {
      setIsSelected(false);
    }

    if (typeof window !== "undefined") {
      let place_top = document.getElementById("place_auto_top");
      if (place_top) {
        let width_data = window.getComputedStyle(place_top).width;
        setBottomtabwidth(width_data);
      }
    }

    if (searchquery.length === 3) {
      setIsSuggestionClick(false);
    }

    setUserInput(address);
    setAddress(address);
    setQuery(searchquery);

    if (searchquery && searchquery.length === 0) {
      setResults([]);
      setHomeresults([]);
      setIsSuggestionClick(false);
      setActiveSuggestion(0);
      setShowSuggestions(false);
      gethomedataInfo("");
    } else if (
      searchquery &&
      searchquery.length > 2 &&
      is_suggestion_click === false
    ) {
      gethomedataInfo(searchquery);

      if (startsWithNumber(searchquery)) {
        setIsMlsCalling(true);
      }
    }
  };

  // Handle input click - exactly like Layout component
  const handleInputClick = (e) => {
    setShowSuggestions(false);
  };

  // Handle close icon click - exactly like Layout component
  const handleCloseIconClick = () => {
    setResults([]);
    setHomeresults([]);
    setIsSuggestionClick(false);
    setUserInput("");
    setAddress("");
    setShowSuggestions(false);
  };

  // Handle keyboard - exactly like Layout component
  const handleKeyChange = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (query) {
        gethomedataInfo(query);
      }
    }
  };

  // Get city data - modified to prevent cross-tab reload
  const getcitydata_func = (cityname, red_address, statename) => {
    const getdata = {
      start: 0,
      limit: 1,
      order: "DESC",
      orderBy: "npi",
      fields: [{ name: "postalcity_name", value: cityname, operator: "=" }],
    };

    getcitydata(getdata)
      .then((result) => {
        if (result) {
          if (result.data.getcitydata && result.data.getcitydata.length > 0) {
            const cityname = result.data.getcitydata[0].cityfullname;
            window.location.replace(`/City/${cityname}`);
          } else {
            // Don't set ispagemain to avoid cross-tab reload
            window.location.replace(`/?address=${red_address}`);
          }
        }
      })
      .catch((error) => {
        // Don't set ispagemain to avoid cross-tab reload
        window.location.replace(`/?address=${red_address}`);
      });
  };

  // Handle select - modified to prevent cross-tab reload
  const handleSelect = (e, suggestions, type, selectedvalue) => {
    let pathname = window.location.pathname;
    setIsSelected(true);
    setIsLoaded(true);
    localStorage.setItem("prvioussuggestions", JSON.stringify(suggestions));
    setUserInput(e.currentTarget.innerText);

    let red_address = e.currentTarget.innerText;
    if (nhmap) {
      setAddress(red_address);
      if (!isnhmap) {
        if (
          pathname.includes("Neighborhood") ||
          pathname.includes("DetailsListing") ||
          pathname.includes("City")
        ) {
          if (selectedvalue !== "" && selectedvalue !== undefined) {
            if (type === "street") {
              window.location.replace("/DetailsListing/?id=" + selectedvalue);
            } else if (type === "neighborhood") {
              window.location.replace("/Neighborhood/" + selectedvalue);
            } else if (type === "city") {
              let cityname = selectedvalue.split(",")[0];
              let statename = selectedvalue.split(",")[1].trim();
              getcitydata_func(cityname, red_address, statename);
            } else {
              // Don't set ispagemain to avoid cross-tab reload
              window.location.replace("/?address=" + selectedvalue);
            }
          }
        } else {
          // Don't set ispagemain to avoid cross-tab reload
          window.location.replace("/?address=" + red_address);
        }
      } else {
        if (searchOnSelect) {
          searchOnSelect(red_address);
        }
      }
    } else {
      cookie.remove("map-cookie");

      setAddress(red_address);
      if (!isnhmap) {
        if (
          pathname.includes("Neighborhood") ||
          pathname.includes("DetailsListing") ||
          pathname.includes("City")
        ) {
          if (selectedvalue !== "" && selectedvalue !== undefined) {
            if (type === "street") {
              window.location.replace("/DetailsListing/?id=" + selectedvalue);
            } else if (type === "neighborhood") {
              window.location.replace("/Neighborhood/" + selectedvalue);
            } else if (type === "city") {
              let cityname = selectedvalue.split(",")[0];
              let statename = selectedvalue.split(",")[1].trim();
              getcitydata_func(cityname, red_address, statename);
            } else {
              // Don't set ispagemain to avoid cross-tab reload
              window.location.replace("/?address=" + selectedvalue);
            }
          }
        } else {
          // Don't set ispagemain to avoid cross-tab reload
          window.location.replace("/?address=" + red_address);
        }
      } else {
        if (searchOnSelect) {
          searchOnSelect(red_address);
        }
      }
    }
    setShowSuggestions(false);
    setActiveSuggestion(0);
    setIsSuggestionClick(true);
  };

  if (!mounted) {
    return null;
  }

  let filladdress = userInput !== "" ? userInput : address;

  return (
    <Menu.Item fitted>
      {gmapsLoaded && (
        <PlacesAutocomplete
          value={filladdress}
          onChange={handleInputChange}
          shouldFetchSuggestions={address.length > 1}
          onError={onError}
          ref={ref}
          searchOptions={{
            componentRestrictions: { country: ["us"] },
          }}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => {
            if (suggestions && suggestions.length > 0 && showSuggestions) {
              suggestiondata.current = suggestions;
            }

            return (
              <div
                style={{
                  margin: nhmap ? "" : "0 auto",
                  width: nhmap ? "" : "100%",
                }}
              >
                <div
                  className={`ui input ${nhmap ? "map_search_box" : ""}`}
                  style={{
                    width: nhmap ? "60%" : "100%",
                    border: "1px solid #767676",
                  }}
                >
                  <div
                    className="ui large left icon input location-search-input"
                    id={nhmap ? "" : "location_search"}
                    style={{ width: "100%" }}
                  >
                    <input
                      type="text"
                      icon="search"
                      iconposition="left"
                      style={{ borderRadius: "0px" }}
                      size="large"
                      {...getInputProps({
                        placeholder: nhmap
                          ? "Enter neighborhood, address or city"
                          : "Search Neighborhood, City, Zip",
                        className: "location-search-input",
                        id: "place_auto_top",
                      })}
                      onClick={handleInputClick}
                      onKeyDown={handleKeyChange}
                      ref={ref}
                    />
                    <i aria-hidden="true" className="search icon"></i>
                    {!is_loaded && (
                      <img
                        src={`${process.env.NEXT_PUBLIC_CDN_ID}static/images/loading.gif`}
                        className="loadericon"
                        title="Loader"
                        alt="Loading"
                      />
                    )}
                  </div>
                  {filladdress && (
                    <AiOutlineCloseCircle
                      className="clear-data"
                      onClick={() => handleCloseIconClick("")}
                    />
                  )}
                </div>

                {address.length > 0 && (
                  <div
                    className="autocomplete-dropdown-container"
                    style={{
                      width: bottomtabwidth,
                      position: "absolute",
                      zIndex: 999,
                      backgroundColor: "#ffffff",
                      textAlign: "left",
                      top: 56,
                      borderColor:
                        "currentcolor rgb(200, 200, 200) rgb(200, 200, 200)",
                      borderStyle: "none solid",
                      borderWidth: "medium 1px 1px",
                      borderRadius: "0px 0px 4px 4px",
                      left: nhmap ? "" : "",
                    }}
                  >
                    <div className="suggestions_name">
                      {(() => {
                        let localresultdata = [];
                        let dataarr = [];
                        const unique = [];
                        let result = {};
                        let count = 0;
                        let checknodata = false;

                        // Process Google Places suggestions exactly like nav.js
                        if (
                          suggestiondata.current &&
                          suggestiondata.current.length > 0
                        ) {
                          suggestiondata.current.forEach((suggestion) => {
                            if (
                              suggestion.types.includes("premise") ||
                              suggestion.types.includes("route") ||
                              suggestion.types.includes("locality") ||
                              suggestion.types.includes("street_address") ||
                              suggestion.types.includes("university") ||
                              suggestion.types.includes("school") ||
                              suggestion.types.includes("postal_code")
                            ) {
                              if (suggestion.types.includes("postal_code")) {
                                localresultdata.push({
                                  label:
                                    suggestion.formattedSuggestion.mainText +
                                    " " +
                                    suggestion.formattedSuggestion
                                      .secondaryText,
                                  _type: suggestion.types,
                                });
                              } else {
                                localresultdata.push({
                                  label: suggestion.description,
                                  _type: suggestion.types,
                                });
                              }
                            }
                          });
                        }

                        // Add GraphQL results from props - exactly like nav.js
                        if (results && results.length > 0) {
                          results.forEach((item) => {
                            localresultdata.push(item);
                          });
                        }

                        if (homeresults && homeresults.length > 0) {
                          homeresults.forEach((item) => {
                            localresultdata.push(item);
                          });
                        }

                        // Process and categorize - exactly like nav.js
                        if (localresultdata.length > 0) {
                          localresultdata.forEach((item) => {
                            let homeaddress = "";
                            if (item.UnparsedAddress) {
                              homeaddress =
                                item.UnparsedAddress +
                                ", " +
                                item.City +
                                ", " +
                                item.StateOrProvince;
                              if (item.PostalCode !== null) {
                                homeaddress += ", " + item.PostalCode;
                              }
                            }

                            // Neighborhood
                            if (
                              showSuggestions &&
                              item._type === undefined &&
                              item.label !== undefined
                            ) {
                              dataarr.push({
                                name: item.label,
                                category: "neighborhood",
                                id: item.id,
                                nhfullname: item.nhfullname,
                              });
                            }

                            // Zipcode
                            if (
                              item._type &&
                              showSuggestions &&
                              item._type.includes("postal_code")
                            ) {
                              dataarr.push({
                                name: item.label,
                                category: "zipcode",
                              });
                            }

                            // School
                            if (
                              showSuggestions &&
                              item._type === undefined &&
                              item.School !== undefined
                            ) {
                              let schoolname = "";
                              schoolname += item.School;
                              if (item.City) {
                                schoolname += ", " + item.City;
                              }
                              if (item.State) {
                                schoolname += ", " + item.State;
                              }

                              dataarr.push({
                                name: schoolname,
                                category: "school",
                                id: item.id,
                              });
                            }

                            // Address
                            if (
                              showSuggestions &&
                              item._type === undefined &&
                              item.UnparsedAddress !== undefined
                            ) {
                              dataarr.push({
                                name: homeaddress,
                                category: "address",
                                id: item.ListingKeyNumeric,
                              });
                            } else if (
                              item._type &&
                              showSuggestions &&
                              item._type.includes("locality")
                            ) {
                              dataarr.push({
                                name: item.label,
                                category: "locality",
                              });
                            }

                            // Google addresses
                            if (
                              item._type &&
                              showSuggestions &&
                              (item._type.includes("street_address") ||
                                item._type.includes("premise") ||
                                item._type.includes("route"))
                            ) {
                              dataarr.push({
                                name: item.label,
                                category: "googleaddress",
                              });
                            }

                            // University
                            if (
                              item._type &&
                              showSuggestions &&
                              item._type.includes("university")
                            ) {
                              dataarr.push({
                                name: item.label,
                                category: "university",
                              });
                            }
                          });

                          // Remove duplicates - exactly like nav.js
                          dataarr.length > 0 &&
                            dataarr.forEach((x) =>
                              unique.filter((a) => a.name == x.name).length > 0
                                ? null
                                : unique.push(x),
                            );

                          // Filter by query - exactly like nav.js
                          let filterdarr = unique.filter(
                            (x) =>
                              x.name
                                .toLowerCase()
                                .indexOf(query.toLowerCase()) === 0,
                          );

                          // Group by category - exactly like nav.js
                          result =
                            filterdarr.length > 0 &&
                            filterdarr.reduce(function (r, a) {
                              r[a.category] = r[a.category] || [];
                              r[a.category].push(a);
                              return r;
                            }, Object.create(null));
                        }

                        return (
                          <>
                            {/* NEIGHBORHOOD Section */}
                            {result &&
                              result.neighborhood &&
                              result.neighborhood.length > 0 &&
                              result.neighborhood.map((item, index) => {
                                checknodata = true;
                                count++;
                                return (
                                  <React.Fragment key={`neighborhood-${index}`}>
                                    {index === 0 && (
                                      <div className="nh_search">
                                        <Icon name="home" /> Neighborhood
                                      </div>
                                    )}
                                    <p
                                      className={
                                        item.name === userInput
                                          ? "suggestion-active"
                                          : "suggestions_name"
                                      }
                                      id={count}
                                      onClick={(e) =>
                                        handleSelect(
                                          e,
                                          suggestiondata.current,
                                          "neighborhood",
                                          item.nhfullname,
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {item.name}
                                    </p>

                                    {index + 1 ===
                                      result.neighborhood.length && (
                                      <div
                                        style={{
                                          borderBottom:
                                            "1px solid rgb(200, 200, 200)",
                                        }}
                                      ></div>
                                    )}
                                  </React.Fragment>
                                );
                              })}

                            {/* ZIPCODE Section */}
                            {result &&
                              result.zipcode &&
                              result.zipcode.length > 0 &&
                              result.zipcode.map((item, index) => {
                                checknodata = true;
                                count++;
                                return (
                                  <React.Fragment key={`zipcode-${index}`}>
                                    {index === 0 && (
                                      <div className="nh_search">
                                        <Icon name="home" /> Zipcode
                                      </div>
                                    )}
                                    <p
                                      className={
                                        item.name === userInput
                                          ? "suggestion-active"
                                          : "suggestions_name"
                                      }
                                      id={count}
                                      onClick={(e) =>
                                        handleSelect(
                                          e,
                                          suggestiondata.current,
                                          "neighborhood",
                                          item.name,
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {item.name}
                                    </p>

                                    {index + 1 === result.zipcode.length && (
                                      <div
                                        style={{
                                          borderBottom:
                                            "1px solid rgb(200, 200, 200)",
                                        }}
                                      ></div>
                                    )}
                                  </React.Fragment>
                                );
                              })}

                            {/* ADDRESS Section */}
                            {result &&
                              result.address &&
                              result.address.length > 0 &&
                              result.address.map((item, index) => {
                                checknodata = true;
                                count++;
                                return (
                                  <React.Fragment key={`address-${index}`}>
                                    {index === 0 && (
                                      <div className="nh_search">
                                        <Icon name="street view" /> Listing
                                        Addresses
                                      </div>
                                    )}
                                    <p
                                      className={
                                        item.name === userInput
                                          ? "suggestion-active"
                                          : "suggestions_name"
                                      }
                                      id={count}
                                      onClick={(e) =>
                                        handleSelect(
                                          e,
                                          suggestiondata.current,
                                          item.id ? "street" : "",
                                          item.id ? item.id : item.name,
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {item.name}
                                    </p>
                                    {index + 1 === result.address.length && (
                                      <div
                                        style={{
                                          borderBottom:
                                            "1px solid rgb(200, 200, 200)",
                                        }}
                                      ></div>
                                    )}
                                  </React.Fragment>
                                );
                              })}

                            {/* LOCALITY (City) Section */}
                            {result &&
                              result.locality &&
                              result.locality.length > 0 &&
                              result.locality.map((item, index) => {
                                checknodata = true;
                                count++;
                                return (
                                  <React.Fragment key={`locality-${index}`}>
                                    {index === 0 && (
                                      <div className="nh_search">
                                        <Icon name="building" /> City
                                      </div>
                                    )}
                                    <p
                                      className={
                                        item.name === userInput
                                          ? "suggestion-active"
                                          : "suggestions_name"
                                      }
                                      id={count}
                                      onClick={(e) =>
                                        handleSelect(
                                          e,
                                          suggestiondata.current,
                                          "city",
                                          item.name,
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {item.name}
                                    </p>
                                    {index + 1 === result.locality.length && (
                                      <div
                                        style={{
                                          borderBottom:
                                            "1px solid rgb(200, 200, 200)",
                                        }}
                                      ></div>
                                    )}
                                  </React.Fragment>
                                );
                              })}

                            {/* UNIVERSITY Section */}
                            {result &&
                              result.university &&
                              result.university.length > 0 &&
                              result.university.map((item, index) => {
                                checknodata = true;
                                count++;
                                return (
                                  <React.Fragment key={`university-${index}`}>
                                    {index === 0 && (
                                      <div className="nh_search">
                                        <Icon name="university" /> University
                                      </div>
                                    )}
                                    <p
                                      className={
                                        item.name === userInput
                                          ? "suggestion-active"
                                          : "suggestions_name"
                                      }
                                      id={count}
                                      onClick={(e) =>
                                        handleSelect(
                                          e,
                                          suggestiondata.current,
                                          "university",
                                          item.name,
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {item.name}
                                    </p>
                                    {index + 1 === result.university.length && (
                                      <div
                                        style={{
                                          borderBottom:
                                            "1px solid rgb(200, 200, 200)",
                                        }}
                                      ></div>
                                    )}
                                  </React.Fragment>
                                );
                              })}

                            {/* SCHOOL Section */}
                            {result &&
                              result.school &&
                              result.school.length > 0 &&
                              result.school.map((item, index) => {
                                checknodata = true;
                                count++;
                                return (
                                  <React.Fragment key={`school-${index}`}>
                                    {index === 0 && (
                                      <div className="nh_search">
                                        <Icon name="graduation cap" /> School
                                      </div>
                                    )}
                                    <p
                                      className={
                                        item.name === userInput
                                          ? "suggestion-active"
                                          : "suggestions_name"
                                      }
                                      id={count}
                                      onClick={(e) =>
                                        handleSelect(
                                          e,
                                          suggestiondata.current,
                                          "school",
                                          item.name,
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {item.name}
                                    </p>
                                    {index + 1 === result.school.length && (
                                      <div
                                        style={{
                                          borderBottom:
                                            "1px solid rgb(200, 200, 200)",
                                        }}
                                      ></div>
                                    )}
                                  </React.Fragment>
                                );
                              })}

                            {/* GOOGLE ADDRESS Section */}
                            {result &&
                              result.googleaddress &&
                              result.googleaddress.length > 0 &&
                              result.googleaddress.map((item, index) => {
                                checknodata = true;
                                count++;
                                return (
                                  <React.Fragment
                                    key={`googleaddress-${index}`}
                                  >
                                    {index === 0 && (
                                      <div className="nh_search">
                                        <Icon name="street view" /> All
                                        Addresses
                                      </div>
                                    )}
                                    <p
                                      className={
                                        item.name === userInput
                                          ? "suggestion-active"
                                          : "suggestions_name"
                                      }
                                      id={count}
                                      onClick={(e) =>
                                        handleSelect(
                                          e,
                                          suggestiondata.current,
                                          "googleaddress",
                                          item.name,
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {item.name}
                                    </p>
                                    {index + 1 ===
                                      result.googleaddress.length && (
                                      <div
                                        style={{
                                          borderBottom:
                                            "1px solid rgb(200, 200, 200)",
                                        }}
                                      ></div>
                                    )}
                                  </React.Fragment>
                                );
                              })}

                            {/* No results message */}
                            {!checknodata &&
                              is_loaded &&
                              address.length > 2 &&
                              showSuggestions && (
                                <p className="no_result_searchbox suggestions_name">
                                  No result found
                                </p>
                              )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            );
          }}
        </PlacesAutocomplete>
      )}
    </Menu.Item>
  );
};

export default ClientSearchBar;
